/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Dimension } from '#utils/UNSAFE_visTypes/common';
import type {
  Group,
  DragStartDetail,
  ChartSeriesData,
  DropDetail,
  ChartItemData
} from '#utils/UNSAFE_visTypes/chart';
import { getInfo, inSpace } from '#utils/UNSAFE_visUtils';
import type { RefObject } from 'preact';
import { findSeriesGroupIndexFromId } from '#utils/PRIVATE_chartUtils/utils';
import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';

export function getChartDragContext<K, D extends ChartItemData<K>>(
  target: HTMLElement,
  levelsArray: GroupLevelItem[][],
  rootRef: RefObject<HTMLElement>,
  event: DragEvent,
  selectedIds: Set<K>,
  series: ChartSeriesData<K, D>[],
  getDataItem?: (seriesIndex: number, groupIndex: number, groupPath: number[]) => D | undefined,
  groupDraggable?: (group: Group) => 'on' | 'off',
  itemsDraggable?: (data: D, seriesIndex: number, groupIndex: number) => 'on' | 'off'
) {
  //We search for the key using the target of the event
  const info = getInfo(rootRef, target);
  const seriesIndex = info ? info.seriesIndex : undefined;
  const groupIndex = info ? info.groupIndex : undefined;
  const levelIndex = info ? info.levelIndex : undefined;

  let context;
  if (
    seriesIndex !== undefined &&
    groupIndex !== undefined &&
    getDataItem?.(seriesIndex, groupIndex, levelsArray[levelsArray.length - 1]?.[groupIndex].path)
  ) {
    const currentData = getDataItem(
      seriesIndex,
      groupIndex,
      levelsArray[levelsArray.length - 1]?.[groupIndex].path
    )! as D;
    if (itemsDraggable && itemsDraggable(currentData, seriesIndex, groupIndex) == 'on') {
      const data = [];
      if (selectedIds.size > 1 && selectedIds.has(currentData.id)) {
        const iterator = selectedIds.values();
        let current = iterator.next().value;
        while (current !== undefined) {
          const { seriesIndex, groupIndex } = findSeriesGroupIndexFromId(
            series,
            current,
            levelsArray,
            getDataItem
          );
          data.push({
            data: getDataItem(
              seriesIndex,
              groupIndex,
              levelsArray[levelsArray.length - 1]?.[groupIndex].path
            ),
            seriesIndex,
            groupIndex,
            groupPath: levelsArray[levelsArray.length - 1]?.[groupIndex].path
          });
          current = iterator.next().value;
        }
      } else {
        data.push({
          data: getDataItem(
            seriesIndex,
            groupIndex,
            levelsArray[levelsArray.length - 1]?.[groupIndex]?.path
          ),
          seriesIndex,
          groupIndex,
          groupPath: levelsArray[levelsArray.length - 1]?.[groupIndex]?.path
        });
      }
      context = {
        items: data,
        type: 'items',
        event
      } as DragStartDetail<D>;
    }
  } else if (groupIndex !== undefined && levelIndex != undefined) {
    if (groupDraggable && groupDraggable(levelsArray[levelIndex][groupIndex].item)) {
      context = {
        group: levelsArray[levelIndex][groupIndex].item,
        groupIndex: groupIndex,
        type: 'xAxisTickLabel',
        event
      } as DragStartDetail<D>;
    }
  }
  return context as DragStartDetail<D>;
}

export function getChartDropContext(
  xSpace: Dimension,
  ySpace: Dimension,
  plotAreaSpace: Dimension,
  event: DragEvent
) {
  //We search for the key using the target of the event
  const offSetX = event.offsetX;
  const offSetY = event.offsetY;
  let context;
  if (inSpace(offSetX, offSetY, xSpace)) {
    context = {
      type: 'xAxis',
      x: offSetX - xSpace.x,
      event
    };
  } else if (inSpace(offSetX, offSetY, ySpace)) {
    context = {
      type: 'yAxis',
      y: offSetY - ySpace.y,
      event
    };
  } else if (inSpace(offSetX, offSetY, plotAreaSpace)) {
    context = {
      type: 'plotArea',
      x: offSetX - plotAreaSpace.x,
      y: offSetY - plotAreaSpace.y,
      event
    };
  }
  return context as DropDetail;
}

export function isChartDraggable<K, D>(
  series: ChartSeriesData<K, D>[],
  levelsArray: GroupLevelItem[][],
  getDataItem: (seriesIndex: number, groupIndex: number, groupPath: number[]) => D | undefined,
  groupDraggable?: (group: Group) => 'on' | 'off',
  itemsDraggable?: (data: D, seriesIndex: number, groupIndex: number) => 'on' | 'off'
) {
  if (groupDraggable) {
    for (let levelIndex = 0; levelIndex < levelsArray.length; levelIndex++) {
      const groupItems = levelsArray[levelIndex];
      for (let groupIndex = 0; groupIndex < groupItems.length; groupIndex++) {
        if (groupDraggable(levelsArray[levelIndex][groupIndex].item) === 'on') {
          return true;
        }
      }
    }
  }

  if (itemsDraggable && getDataItem) {
    const leafgroups = levelsArray[levelsArray.length - 1];
    for (let i = 0; i < leafgroups.length; i++) {
      for (let j = 0; j < series.length; j++) {
        if (
          getDataItem(j, i, leafgroups[i].path) &&
          itemsDraggable(getDataItem(j, i, leafgroups[i].path)!, j, i) === 'on'
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

export function getDragImageElements<D>(
  rootRef: RefObject<Element>,
  context: DragStartDetail<D>,
  event: DragEvent
) {
  const elems = [];
  let elem;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  const container = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  let width;
  let height;

  if (context?.type === 'items') {
    const items = context.items;
    // only component specific
    for (let i = 0; i < items.length; i++) {
      elem = rootRef.current?.querySelector(
        `[data-oj-private-series-index="${items[i].seriesIndex}"][data-oj-private-group-index="${items[i].groupIndex}"]`
      );
      if (elem) elems.push(elem);
    }
  } else if (context?.type === 'xAxisTickLabel') {
    elem = rootRef.current?.querySelector(`[data-oj-private-group-index="${context?.groupIndex}"]`);
    if (elem) elems.push(elem);
  }
  if (elems.length > 0) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const bboxs = [];
    for (let i = 0; i < elems.length; i++) {
      const bbox = (elems[i] as SVGGraphicsElement).getBBox();
      if (bbox.x < minX) {
        minX = bbox.x;
      }
      if (bbox.x + bbox.width > maxX) {
        maxX = bbox.x + bbox.width;
      }
      if (bbox.y < minY) {
        minY = bbox.y;
      }
      if (bbox.y + bbox.height > maxY) {
        maxY = bbox.y + bbox.height;
      }
      bboxs.push(bbox);
    }
    // Call getComputedStyles for font on the rootref
    const styles = window.getComputedStyle(elems[0].parentElement!);
    for (let i = 0; i < elems.length; i++) {
      elem = elems[i];
      const clone = elem.cloneNode(true) as SVGGraphicsElement;
      container.appendChild(clone);
    }
    container.setAttribute('transform', `translate(${-minX},${-minY})`);
    container.style.fontSize = styles.getPropertyValue('font-size');
    container.style.fontFamily = styles.getPropertyValue('font-family');
    container.style.textAnchor = styles.getPropertyValue('text-anchor');
    container.style.dominantBaseline = styles.getPropertyValue('dominant-baseline');
    width = maxX - minX;
    height = maxY - minY;
    svg.appendChild(container);
    return {
      image: svg,
      offSetX: event.offsetX - minX,
      offSetY: event.offsetY - minY,
      width,
      height
    };
  }
  return null;
}
